
import { readHistories, writeHistory, clearHistories } from './search-history'
import { debounce, removeEmpty } from '~/utils/tools'
import {
  searchLazyAssociate,
  searchPlaceholder,
  searchTrendingKeywords,
  sceneTypeDesc,
} from '~/api/library-center/common'

export default {
  name: 'LibSearchInput',
  props: {
    // 搜索框默认输入值
    defaultValue: {
      type: String,
      default: '',
    },
    // 答卷类型选择，默认选择全部
    ptype: {
      type: String,
      default: '',
    },
    // 当未配置关键词，且用户未输入值，点击搜索按钮(enter键入)执行的自定义逻辑，默认是重刷当前页
    customJumpAction: {
      type: Function,
      default: null,
    },
    // 热门文字的颜色
    hotTextColor: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      keyword: '', // 后台配置的关键词
      totalLibs: 0, // keyword不存在时，显示的模板数量占位符
      focusStyle: false,
      lightFoucsStyle: false,
      isSearch: false,
      hotList: [],
      historyList: [],
      shouldSearchingVisible: false,
      searchingList: [],
      searchVal: '',
      libType: '',
      libTypeOptions: [{ label: '全部', value: '' }],
      hotWord: [],
      hasEdited: false,
    }
  },
  async fetch() {
    try {
      await Promise.all([this.fetchSearchDefaultOpts(), this.fetchlibTypeOptions()])
    } catch (error) {
      console.log('fetch error: ', error)
    }
  },
  computed: {
    // 是否配置过关键词，如果配置过，则placeholder显示关键词，并且当value为空，点击跳转，取关键词作为用户输入值
    hasConfigedKeyword() {
      return this.keyword.trim() !== ''
    },
    inputPlaceholder() {
      if (this.hasConfigedKeyword) {
        return this.keyword
      }
      return `${this.totalLibs}份模板任你选用`
    },
    shouldHotVisible() {
      return this.hotList.length > 0
    },
    shouldHistoryVisible() {
      return this.historyList.length > 0
    },
  },
  watch: {
    defaultValue(v) {
      this.searchVal = v
    },
    ptype(v) {
      this.libType = v
    },
    searchVal(v) {
      this.judgeShowSearchStyle()
      if (v === '') {
        this.toggleSearchBetweenRecommand(false)
      } else {
        this.toggleSearchBetweenRecommand(true)
        this.fetchSearching(v)
      }
      // 用户未输入过，不展示下拉框
      if (!this.hasEdited) {
        this.closeSearch()
      }
    },
  },
  mounted() {
    this.fromUrlToSet()
    this.fetchHotSearch()
  },
  methods: {
    // 获取搜索框的默认配置，后台配置
    async fetchSearchDefaultOpts() {
      try {
        const { data } = await searchPlaceholder()
        const { project_library_count, set_keyword } = data
        this.keyword = set_keyword
        this.totalLibs = project_library_count
      } catch (error) {
        return Promise.reject(error)
      }
    },
    // 获取问卷类型
    async fetchlibTypeOptions() {
      try {
        const { data } = await sceneTypeDesc()
        this.libTypeOptions.splice(1)
        this.libTypeOptions.push(
          ...data.map((item) => ({
            label: item.title,
            value: item.scene_number,
          }))
        )
      } catch (error) {
        return Promise.reject(error)
      }
    },
    // 初始化热门搜索列表
    async fetchHotSearch() {
      try {
        const { data } = await searchTrendingKeywords()
        this.hotList = data
      } catch (error) {
        return Promise.reject(error)
      }
    },
    // 联想词搜索，600ms防抖
    fetchSearching: debounce(async function (v) {
      try {
        const { data } = await searchLazyAssociate(v)
        this.searchingList = this.adjustSearchingResult(data.slice(0, 5))

        // 如果没有匹配的联想词，则不展示下拉框
        // 用户未输入过，不展示下拉框
        if (this.hasEdited) {
          this.judgeShowSearchStyle()
        }
      } catch (error) {
        return Promise.reject(error)
      }
    }, 600),
    // 修正联想词搜索结果，高亮匹配输入词，并显示是否为官方标签
    adjustSearchingResult(v) {
      return v.map((item) => {
        const hightlightRule = new RegExp(this.searchVal, 'g')
        const hightlightPart = `<span style='color: #2672FF;'>${this.searchVal}</span>`
        const replacedLabel = item.title.replace(hightlightRule, hightlightPart)
        return {
          rawLabel: item.title,
          label: replacedLabel,
          isOfficial: item.is_official,
          quoted: item.ref_count,
          ...item,
        }
      })
    },
    // toggle联想搜索框显示隐藏
    toggleSearchBetweenRecommand(flag) {
      if (typeof flag == 'boolean') {
        this.shouldSearchingVisible = flag
      } else {
        this.shouldSearchingVisible = !this.shouldSearchingVisible
      }
    },
    // 打开搜索下拉框
    openSearch() {
      this.historyList = readHistories().map((item) => item.text)
      this.judgeShowSearchStyle()
    },
    // 根据输入框值和联想结果来动态判断，是否显示focus样式、打开结果面板
    judgeShowSearchStyle() {
      this.focusStyle = !(this.searchVal && this.searchingList.length === 0)
      this.lightFoucsStyle = !this.focusStyle
      this.isSearch = !(this.searchVal && this.searchingList.length === 0)
    },
    // 关闭搜索下拉框
    closeSearch() {
      this.focusStyle = false
      this.lightFoucsStyle = false
      this.isSearch = false
    },
    // 清除历史搜索记录
    clearHistoryList() {
      this.historyList = []
      clearHistories()
    },
    // 跳转链接，并存储历史记录
    jumpSearch() {
      const syncHistory = (val) => {
        writeHistory({ text: val, timestamp: new Date().getTime() })
        this.historyList = readHistories().map((item) => item.text)
        const typeQuery = this.libType ? { scene: this.libType } : {}
        this.$href('/search/', { keyword: val, ...typeQuery })
      }

      // 用户未输入值 && 配置了关键词，取placeholder关键词
      if (this.hasConfigedKeyword && this.searchVal.trim() === '') {
        syncHistory(this.keyword)
        // 用户输入了值，取用户输入值
      } else if (this.searchVal.trim() !== '') {
        syncHistory(this.searchVal)
      } else {
        // 用户没输入值，也没有配置关键词，则走自定义跳转逻辑
        if (this.customJumpAction) return this.customJumpAction()
        const { path } = this.$route
        // ● 不配置默认搜索词时，大搜索框直接进行搜索的，直接刷新页面；如果是在/search/页面的大搜索框时，直接搜索需要重定向至/lib/
        if (path === '/search/') {
          this.$href('/lib/')
        } else {
          window.location.reload()
        }
      }
    },
    // 更新搜索词
    updateSearchVal(val) {
      this.searchVal = val.trim()
      this.jumpSearch()
    },
    // 点击联想词，跳转到模板详情页
    jumpLibDetail(libInfo) {
      writeHistory({ text: libInfo.rawLabel, timestamp: new Date().getTime() })
      window.open(`/lib_detail_full/${libInfo.id}`, '_blank')
    },
    // 获取URL 上的地址
    fromUrlToSet() {
      const params = removeEmpty(this.$route.query)
      // 兼容历史版本字段
      if (params.scene || params.scene === 0 || params.ptype || params.ptype === 0)
        this.libType = Number.parseInt(params.scene)
      if (params.keyword) this.searchVal = params.keyword
      if (params.keywords) this.searchVal = params.keywords
    },
  },
}
